import { ApiType } from 'src/Types/ApiType'
import { AssetType } from 'src/Types/AssetTypes'

import type { DateRange } from 'src/Types/DatePeriod'
import type { AssetWithAlarms } from 'src/Types/AssetWithAlarms'
import { InternalAlarmSystem } from 'src/Types/AlarmSystem'
import type { NewTrackCircuit, OrchestratedTrackCircuit } from 'src/Types/NewTrackCircuit'
import type { GetOrchestratedAssetsParams } from 'src/Types/GetOrchestratedAssetsParams'

import { callApiGet } from 'src/msGraphClient'
import { getCriticalityValue } from 'src/Utils/criticality'
import { getOrchestratedAssetIdParams } from 'src/Utils/asset'
import config from 'src/Config/Config'

const { ORCHESTRATED_TRACK_CIRCUITS } = config.API_FRAGMENTS.apim

const transformIntoSparseResponse = (res: OrchestratedTrackCircuit[]) =>
  res.map((entry, index) => {
    const mod = index % 3

    let sparseEntry: OrchestratedTrackCircuit

    if (mod === 0) {
      // Variant 1: Nullify fromKm and toKm
      sparseEntry = {
        ...entry,
        assetData: {
          ...entry.assetData,
          name: `${entry.assetData.name} (*1)`,
        },
        trackCircuitAssetData: {
          ...entry.trackCircuitAssetData,
          // @ts-expect-error We know fromKm is a number, but we want to simulate sparse data
          fromKm: null,
          // @ts-expect-error We know fromKm is a number, but we want to simulate sparse data
          toKm: null,
        },
      }
    } else if (mod === 1) {
      // Variant 2: Set name to '<null>'
      sparseEntry = {
        ...entry,
        assetData: {
          ...entry.assetData,
          name: `${entry.assetData.name} (*2)`,
          /* @ts-expect-error Yes, we force it */
          locationName: null,
        },
      }
    } else {
      // Variant 3: Leave unchanged
      sparseEntry = entry
    }

    return sparseEntry
  })

const synthesizeSparseData = (fromDate: string | number | Date, toDate: string | number | Date): OrchestratedTrackCircuit[] => {
  const count = 10
  const from = new Date(fromDate).getTime()
  const to = new Date(toDate).getTime()

  return Array.from({ length: count }, (_, i) => {
    const mod = i % 3
    const randomTimestamp = new Date(from + Math.random() * (to - from)).getTime()

    // Fill all required fields, no extras
    let entry: OrchestratedTrackCircuit = {
      assetData: {
        baneDataId: `synth-${i}`,
        name: `Synthetic TC ${i}`,
        locationName: `SynthLoc ${i}`,
        locationId: `synth-loc-${i}`,
        domain: 'TrackCircuit', // Use the correct property if required
      },
      trackCircuitAssetData: {
        fromKm: Math.random() * 100,
        toKm: Math.random() * 100 + 100,
        length: 100,
        relayType: 'N/A',
        trackCircuitTpe: 'N/A',
        neighbors: [],
        m40TrackCircuit: {
          bnM40Id: `BNM40-${i}`,
          sensorStation: `SensorStation-${i}`,
          tcId: `TC${i}`,
        },
      },
      status: {
        isMonitored: true,
        assetStatusWindows: [],
        statusWithoutAssetStatusWindows: 'Alarm',
        statusWithAssetStatusWindows: 'Alarm',
        showCriticality: i % 2 === 0,
        dailyStatus: [
          {
            statusWithoutAssetStatusWindows: 'Alarm',
            statusWithAssetStatusWindows: 'Alarm',
            fromDateTime: randomTimestamp - 1000 * 60 * 60, // 1 hour before
            toDateTime: randomTimestamp,
          },
        ],
      },
      aggregateAlarms: [],
    }

    if (mod === 0) {
      entry = {
        ...entry,
        assetData: {
          ...entry.assetData,
          name: `${entry.assetData.name} (*1)`,
        },
        trackCircuitAssetData: {
          ...entry.trackCircuitAssetData,
          fromKm: null as any,
          toKm: null as any,
        },
      }
    } else if (mod === 1) {
      entry = {
        ...entry,
        assetData: {
          ...entry.assetData,
          name: `${entry.assetData.name} (*2)`,
          locationName: null as any,
        },
      }
    }

    return entry
  })
}

export const getOrchestratedTrackCircuits = (params: GetOrchestratedAssetsParams): Promise<AssetWithAlarms[]> => {
  const urlParams = new URLSearchParams({
    fromDate: new Date(params.dateRange.fromDate).toJSON(),
    toDate: new Date(params.dateRange.toDate).toJSON(),
    onlyWithAlarms: params.onlyWithAlarms.toString(),
    feFilterEnabled: (!params.enableAllAlarmTypes).toString(),
  })

  const { idKey, value } = getOrchestratedAssetIdParams(params)
  urlParams.set(idKey, value)

  return callApiGet<OrchestratedTrackCircuit[]>(`${ORCHESTRATED_TRACK_CIRCUITS}?${urlParams.toString()}`, ApiType.APIM, {
    parseDates: true,
  })
    .then(res => {
      if (params.synthSparseResponseData) {
        console.log('Synthesizing sparse data')
        const synthesizedData = synthesizeSparseData(params.dateRange.fromDate, params.dateRange.toDate)
        console.log('Synthesized data:', synthesizedData)
        return synthesizedData
      }
      if (params.fakeSparseResponseData) {
        console.log('Transforming into sparse response data')
        return transformIntoSparseResponse(res)
      }
      return res
    })
    .then(res =>
      res.map<AssetWithAlarms>(entry => {
        const asset: NewTrackCircuit = {
          assetType: AssetType.TrackCircuit,
          baneDataId: entry.assetData.baneDataId,
          name: entry.assetData.name,
          baneDataLocationName: entry.assetData.locationName,
          baneDataLocationId: entry.assetData.locationId,
          fromKm: entry.trackCircuitAssetData.fromKm,
          toKm: entry.trackCircuitAssetData.toKm,
          isMonitored: entry.status.isMonitored,
          componentCriticalityLocal: getCriticalityValue(entry.assetData, 'ComponentCriticalityLocal'),
          alarmCriticalityAbsolute: getCriticalityValue(entry.assetData, 'AlarmCriticalityAbsolute'),
          totalCriticalityAbsolute: getCriticalityValue(entry.assetData, 'TotalCriticalityAbsolute'),
          statusWithoutAssetStatusWindows: entry.status.statusWithoutAssetStatusWindows,
          statusWithAssetStatusWindows: entry.status.statusWithAssetStatusWindows,
          showCriticality: entry.status.showCriticality,
          dailyStatus: entry.status.dailyStatus,
          assetStatusWindows: entry.status.assetStatusWindows,
          trackCircuitAssetData: entry.trackCircuitAssetData,
        }

        return {
          asset,
          alarms: entry.aggregateAlarms.map(alarm => ({
            ...alarm,
            internalAlarmSystem: InternalAlarmSystem.AggregatedTrackCircuitAlarm,
          })),
        }
      })
    )
    .then(result => result.sort((a, b) => a.asset.name.localeCompare(b.asset.name, config.STRING_LOCALE)))
}

export const getTrackCircuitsAtLocations = (
  dateRange: DateRange,
  locationIds: string[],
  onlyWithAlarms: boolean,
  enableAllAlarmTypes: boolean,
  fakeSparseResponseData: boolean = false,
  synthSparseResponseData: boolean = false
) =>
  getOrchestratedTrackCircuits({
    dateRange,
    locationIds,
    onlyWithAlarms,
    enableAllAlarmTypes,
    fakeSparseResponseData,
    synthSparseResponseData,
  })

export const getTrackCircuitsWithIds = (
  dateRange: DateRange,
  baneDataIds: string[],
  onlyWithAlarms: boolean,
  enableAllAlarmTypes: boolean = true,
  fakeSparseResponseData: boolean = false,
  synthSparseResponseData: boolean = false
) =>
  getOrchestratedTrackCircuits({
    dateRange,
    baneDataIds,
    onlyWithAlarms,
    enableAllAlarmTypes,
    fakeSparseResponseData,
    synthSparseResponseData,
  })
